// src/hooks/useWeightCalculations.js
import { useMemo } from 'react';
import {
  calculateBMI,
  calculateAverageWeeklyLoss,
  calculateTotalWeightLoss,
  calculateEstimatedGoalDate,
  DEFAULT_AVG_LOSS
} from '../utilities/weightCalculations';

export function useWeightCalculations({
  height,
  weight,
  goalWeight,
  startDate,
  historyData = [],
  useTodayAsStart = false,      // ← NEW PARAMETER
}) {
  // parse floats once
  const heightNum = parseFloat(height);
  const weightNum = parseFloat(weight);
  const goalWeightNum = parseFloat(goalWeight);

  // 1) BMI
  const bmi = useMemo(() => {
    return calculateBMI(heightNum, weightNum); 
  }, [heightNum, weightNum]);

  // 2) average lbs/week
  const averageLossPerWeek = useMemo(() => {
    const avg = calculateAverageWeeklyLoss(startDate, historyData);
    return avg != null ? avg : DEFAULT_AVG_LOSS;
  }, [startDate, historyData]);

  // 3) total lbs lost
  const totalWeightLost = useMemo(() => {
    const total = calculateTotalWeightLoss(startDate, historyData);
    if (total != null) return total;
    // fallback: difference between first & last history if available
    if (historyData.length >= 2) {
      const sorted = [...historyData].sort((a,b) => new Date(a.reported_date) - new Date(b.reported_date));
      return parseFloat(sorted[0].weight) - parseFloat(sorted[sorted.length-1].weight);
    }
    return null;
  }, [startDate, historyData]);

  // 4) projected goal date
  const projectedGoalDateFromStart = useMemo(() => {
    // if there’s no weight or goalWeight, bail early
    if (!weightNum || !goalWeightNum) return '';
    return calculateEstimatedGoalDate(
      useTodayAsStart ? null : startDate, 
      weightNum, 
      goalWeightNum, 
      averageLossPerWeek,
      useTodayAsStart
    );
  }, [startDate, weightNum, goalWeightNum, averageLossPerWeek, useTodayAsStart]);

  return {
    bmi,
    averageLossPerWeek,
    totalWeightLost,
    projectedGoalDateFromStart,
  };
}
